import api_client from '@/repositories';
import tenantRepository from '@/repositories/tenant.repository';
import appConfigService from '@/services/app-config/app-config.service';
import {ICustomQueryArgsRepository} from '@/types/utils/ICustomQueryArgs';
import {useQuery} from '@tanstack/react-query';
import useFirebase from "@/hooks/useFirebase";
import IApplicationConfig from "@/types/IApplicationConfig";

export const QK_APP_CONFIG = 'application-config';

type IUseAppConfigArgs = ICustomQueryArgsRepository<typeof api_client.config.getConfig>;

export function useGetApplicationConfigQuery(args?: IUseAppConfigArgs) {
	const firebaseContext = useFirebase();

	return useQuery({
		queryKey: [QK_APP_CONFIG, firebaseContext?.token ? 'authenticated' : 'public'],
		queryFn: async () => {
			const webAppConfig = await getWebAppConfig(firebaseContext);

			const url = new URL(window.location.href);
			const params = new URLSearchParams(url.search);
			return appConfigService.parseConfigurationFromQueryParams(webAppConfig, params);
		},
		enabled: firebaseContext?.isFirebaseReady, // Only run when Firebase is ready
		...args,
	});
}

async function getWebAppConfig(firebaseContext: { token?: string; isFirebaseReady?: boolean } | undefined): Promise<IApplicationConfig> {
	// If we have a Firebase token, try to get tenant-specific configuration first
	if (firebaseContext?.token && firebaseContext?.isFirebaseReady) {
		try {
			const tenantData = await tenantRepository.get();
			return tenantData.webAppConfig;
		} catch (error) {
			// If authenticated tenant config fails, fall back to public config
			console.warn('Failed to get authenticated tenant config, falling back to public config:', error);
		}
	}

	// Use public configuration (either as fallback or primary choice)
	const publicData = await tenantRepository.getPublicInfo();
	return publicData.webAppConfig;
}
